{
  "pages": [
    {
      "guideType": "learn",
      "slug": "first-prompt",
      "title": "Write your first prompt",
      "description": "Turn a simple request into an instruction AI can use. Practice with a short invitation.",
      "badge": "Your first five lessons",
      "badgeColor": "#040404",
      "navSectionSlug": "module-2-prompting-101",
      "navOrder": 3,
      "navIcon": "message",
      "searchKeywords": [],
      "meta": {
        "title": "How to Write an AI Prompt: Beginner Guide",
        "description": "Write a useful AI prompt using three simple parts: task, context, and format. Includes clear before-and-after examples for beginners.",
        "keywords": "how to write an AI prompt, AI prompts for beginners, prompt writing, prompt engineering basics, good AI prompt example",
        "pageCopy": [
          {
            "type": "section",
            "title": "Start with a real task",
            "copy": "A prompt is the message you give AI. Start with a small task you can judge: an email, a list, or a summary. A clear request makes a useful draft more likely, but you still need to check the result."
          },
          {
            "type": "steps",
            "items": [
              {
                "title": "Name the task",
                "copy": "Start with what you want done: write an invitation, compare two options, or summarize these notes. One clear task is a good first step."
              },
              {
                "title": "Add the details that matter",
                "copy": "For an invitation, give the date, time, location, and audience. AI cannot reliably supply facts you have not given it."
              },
              {
                "title": "Describe the result",
                "copy": "Ask for a short email, a table, or a checklist. Include a tone or length when it matters."
              }
            ]
          },
          {
            "type": "section",
            "title": "Write an invitation people can act on.",
            "copy": "Your neighborhood cleanup is Saturday, 9–11am, at the park entrance. Ask volunteers to bring gloves. Write a prompt for a friendly email under 80 words."
          },
          {
            "type": "prompt-example",
            "before": "Write an email about our cleanup.",
            "after": "Write a friendly email inviting neighbors to a park cleanup on Saturday, 9–11am. Meet at the park entrance and bring gloves. Everyone is welcome. Keep it under 80 words and end by asking people to reply if they can come.",
            "badCaption": "This leaves important details for AI to guess.",
            "goodCaption": "The request gives the task, useful context, and a way to judge the result."
          },
          {
            "type": "section",
            "title": "Try it, then review it",
            "copy": "Open Try it yourself above and write your own version. When you are ready, copy it into Gab AI. Read the answer and ask for one specific improvement."
          },
          {
            "type": "unordered-list",
            "title": "What to check in the result",
            "items": [
              "Does the draft keep Saturday, 9–11am, and the park entrance?",
              "Does it ask people to bring gloves and explain how to reply?",
              "Does it sound welcoming, stay under 80 words, and avoid invented details?"
            ]
          },
          {
            "type": "quiz",
            "items": [
              {
                "question": "Which request gives AI the most useful direction?",
                "options": [
                  "Write an email.",
                  "Write a friendly invitation to our park cleanup on Saturday, 9–11am. Meet at the park entrance, bring gloves, and keep it under 80 words.",
                  "Write the best email ever."
                ],
                "correctIndex": 1,
                "explanation": "The second request supplies the task, relevant facts, and a usable format."
              },
              {
                "question": "The draft adds a free lunch that you never mentioned. What should you do?",
                "options": [
                  "Assume lunch is included.",
                  "Send it because the writing sounds good.",
                  "Remove the invented detail and check the rest against your notes."
                ],
                "correctIndex": 2,
                "explanation": "Fluent writing does not guarantee correct facts. Compare the draft with the information you supplied."
              }
            ]
          },
          {
            "type": "key-takeaways",
            "items": [
              "Name the task.",
              "Add the details that matter.",
              "Describe the result."
            ]
          }
        ],
        "ogImage": "guides/social-cards/be1199b3-4429-4566-a310-37be92f10c5e.png"
      },
      "guideSeo": {
        "readMinutes": 3,
        "difficulty": "Beginner",
        "about": [
          "First AI prompt",
          "Prompt anatomy",
          "Prompting basics"
        ],
        "teaches": [
          "Name the task",
          "Add the details that matter",
          "Describe the result"
        ]
      },
      "navigation": {
        "previous": {
          "label": "Learning home",
          "href": "/docs#learn"
        },
        "next": {
          "label": "Ask for what you need",
          "href": "/docs/learn/being-specific"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/learn/first-prompt",
      "updatedAt": "2026-09-26T12:00:00.000Z"
    },
    {
      "guideType": "learn",
      "slug": "being-specific",
      "title": "Ask for what you need",
      "description": "Tell AI who the result is for, what it must include, and how you want to use it.",
      "badge": "Your first five lessons",
      "badgeColor": "#040404",
      "navSectionSlug": "module-2-prompting-101",
      "navOrder": 4,
      "navIcon": "bullseye",
      "searchKeywords": [],
      "meta": {
        "title": "How to Make AI Prompts More Specific",
        "description": "Get less generic AI answers by specifying the length, format, audience, and tone you need. See weak prompts rewritten into specific ones.",
        "keywords": "how to improve AI prompts, specific AI prompts, better AI answers, prompt format, prompt tone, prompt audience",
        "pageCopy": [
          {
            "type": "section",
            "title": "Start with a real task",
            "copy": "Specific instructions reduce guesswork. You do not need a long prompt or special wording. Add the details that would change the answer: the audience, constraints, and the form of the result."
          },
          {
            "type": "steps",
            "items": [
              {
                "title": "Picture the person using it",
                "copy": "A first-time volunteer needs clear actions. An experienced organizer may need only a short reminder. Tell AI which person you are helping."
              },
              {
                "title": "Name the constraints",
                "copy": "Mention the group size, deadline, available tools, or word limit. Leave out details that have no bearing on the task."
              },
              {
                "title": "Choose a useful shape",
                "copy": "A checklist helps someone act. A table helps compare. A short email helps communicate. Ask for the shape that fits the job."
              }
            ]
          },
          {
            "type": "section",
            "title": "Turn a vague request into a useful brief.",
            "copy": "You need a checklist for a first-time volunteer running a 40-person event. Make the request specific enough that the answer can be used on the day."
          },
          {
            "type": "prompt-example",
            "before": "Help me organize an event.",
            "after": "Create a checklist for a first-time volunteer setting up a 40-person community event. Group it into before guests arrive, during the event, and cleanup. Use short action items. Include seating, signs, step-free access, and a way to record attendance. Flag details I still need to supply.",
            "badCaption": "This leaves important details for AI to guess.",
            "goodCaption": "The request gives the task, useful context, and a way to judge the result."
          },
          {
            "type": "section",
            "title": "Try it, then review it",
            "copy": "Open Try it yourself above and write your own version. When you are ready, copy it into Gab AI. Read the answer and ask for one specific improvement."
          },
          {
            "type": "unordered-list",
            "title": "What to check in the result",
            "items": [
              "Could a new volunteer follow each item without asking what it means?",
              "Does the checklist account for 40 people and step-free access?",
              "Are unknown details flagged for you to fill in?"
            ]
          },
          {
            "type": "quiz",
            "items": [
              {
                "question": "You need instructions a new volunteer can follow. What is most helpful to add?",
                "options": [
                  "Use impressive language.",
                  "Write short action items for a first-time volunteer.",
                  "Make it longer."
                ],
                "correctIndex": 1,
                "explanation": "Naming the audience and the kind of instruction helps AI choose useful detail."
              },
              {
                "question": "What should AI do with a missing event start time?",
                "options": [
                  "Invent a likely time.",
                  "Leave it unspecified and ask for it.",
                  "Ignore all timing."
                ],
                "correctIndex": 1,
                "explanation": "Ask AI to flag missing information. A plausible guess can still be wrong."
              }
            ]
          },
          {
            "type": "key-takeaways",
            "items": [
              "Picture the person using it.",
              "Name the constraints.",
              "Choose a useful shape."
            ]
          }
        ],
        "ogImage": "guides/social-cards/8f387fa9-52bd-472d-8972-f21742e2eee6.png"
      },
      "guideSeo": {
        "readMinutes": 4,
        "difficulty": "Beginner",
        "about": [
          "Prompt specificity",
          "Prompt engineering basics"
        ],
        "teaches": [
          "Picture the person using it",
          "Name the constraints",
          "Choose a useful shape"
        ]
      },
      "navigation": {
        "previous": {
          "label": "Write your first prompt",
          "href": "/docs/learn/first-prompt"
        },
        "next": {
          "label": "Give useful context",
          "href": "/docs/learn/providing-context"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/learn/being-specific",
      "updatedAt": "2026-09-26T12:00:00.000Z"
    },
    {
      "guideType": "learn",
      "slug": "providing-context",
      "title": "Give useful context",
      "description": "Share the background that changes the answer, while keeping private details out of a practice task.",
      "badge": "Your first five lessons",
      "badgeColor": "#040404",
      "navSectionSlug": "module-2-prompting-101",
      "navOrder": 5,
      "navIcon": "layer-group",
      "searchKeywords": [],
      "meta": {
        "title": "How to Give AI Better Context",
        "description": "Learn what context to include in an AI prompt, how to add examples and constraints, and how background information improves the answer.",
        "keywords": "context in AI prompts, how to give AI context, prompt context examples, AI prompt constraints, better AI responses",
        "pageCopy": [
          {
            "type": "section",
            "title": "Start with a real task",
            "copy": "AI sees the information available in the conversation and any files or tools you have chosen to use. It does not automatically know your situation. Give it the facts that make a generic suggestion fit your actual task."
          },
          {
            "type": "steps",
            "items": [
              {
                "title": "Describe the situation",
                "copy": "Say who the work is for and why you need it. For a team lunch, group size and dietary needs matter more than the company name."
              },
              {
                "title": "Make the limits explicit",
                "copy": "Include the time, equipment, budget if you know it, and anything to avoid. If a detail is undecided, say so."
              },
              {
                "title": "Ask about important gaps",
                "copy": "Let AI ask a few questions before it finalizes a recommendation. Decide which answers you need to supply yourself."
              }
            ]
          },
          {
            "type": "section",
            "title": "Give AI the details that change the answer.",
            "copy": "Plan a team lunch for eight people. You have 45 minutes, two vegetarian guests, and a kitchen with no oven. Give AI enough context to suggest a workable plan."
          },
          {
            "type": "prompt-example",
            "before": "Suggest a team lunch.",
            "after": "Suggest three lunch options for eight coworkers. Two guests are vegetarian. We have 45 minutes and a kitchen with a microwave and refrigerator, but no oven. For each option, list what we need to buy and what can be prepared ahead. Ask about any other dietary needs before finalizing the plan.",
            "badCaption": "This leaves important details for AI to guess.",
            "goodCaption": "The request gives the task, useful context, and a way to judge the result."
          },
          {
            "type": "section",
            "title": "Try it, then review it",
            "copy": "Open Try it yourself above and write your own version. When you are ready, copy it into Gab AI. Read the answer and ask for one specific improvement."
          },
          {
            "type": "unordered-list",
            "title": "What to check in the result",
            "items": [
              "Can the lunch be prepared with a microwave and refrigerator, without an oven?",
              "Do the options work for eight people, including two vegetarians?",
              "Does the plan identify other dietary needs to confirm?"
            ]
          },
          {
            "type": "quiz",
            "items": [
              {
                "question": "Which detail most changes a lunch plan?",
                "options": [
                  "The kitchen has no oven.",
                  "The meeting has a catchy name.",
                  "The team likes short emails."
                ],
                "correctIndex": 0,
                "explanation": "Available equipment directly affects which meals are practical."
              },
              {
                "question": "What is a good way to practice with sensitive notes?",
                "options": [
                  "Paste all private details so AI has everything.",
                  "Use a made-up example or remove unnecessary identifying details.",
                  "Ask AI to guess the private details."
                ],
                "correctIndex": 1,
                "explanation": "Practice with invented or de-identified information. Share only information you are comfortable and authorized to provide."
              }
            ]
          },
          {
            "type": "key-takeaways",
            "items": [
              "Describe the situation.",
              "Make the limits explicit.",
              "Ask about important gaps."
            ]
          }
        ],
        "ogImage": "guides/social-cards/63e5df0b-8308-4190-95e9-13d0f37b3ec2.png"
      },
      "guideSeo": {
        "readMinutes": 4,
        "difficulty": "Beginner",
        "about": [
          "Prompt context",
          "AI background information",
          "Prompt engineering"
        ],
        "teaches": [
          "Describe the situation",
          "Make the limits explicit",
          "Ask about important gaps"
        ]
      },
      "navigation": {
        "previous": {
          "label": "Ask for what you need",
          "href": "/docs/learn/being-specific"
        },
        "next": {
          "label": "Improve the first answer",
          "href": "/docs/learn/iterating-prompts"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/learn/providing-context",
      "updatedAt": "2026-09-26T12:00:00.000Z"
    },
    {
      "guideType": "learn",
      "slug": "iterating-prompts",
      "title": "Improve the first answer",
      "description": "Keep what works, name what needs changing, and make one focused revision.",
      "badge": "Your first five lessons",
      "badgeColor": "#040404",
      "navSectionSlug": "module-6-best-practices",
      "navOrder": 21,
      "navIcon": "rotate",
      "searchKeywords": [],
      "meta": {
        "title": "How to Improve an AI Response with Follow-Ups",
        "description": "Treat the first AI answer as a draft. Use follow-up prompts to change length, tone, structure, evidence, and accuracy without starting over.",
        "keywords": "how to improve AI response, follow up prompts, iterate AI prompt, refine AI answer, prompt chaining",
        "pageCopy": [
          {
            "type": "section",
            "title": "Start with a real task",
            "copy": "Treat the first answer as a draft. Useful feedback is specific: what should change, what should stay, and how you will judge the next version. You can keep working in the same conversation so the previous draft stays available."
          },
          {
            "type": "steps",
            "items": [
              {
                "title": "Name the mismatch",
                "copy": "Describe the problem you can see: too formal, too long, missing a location, or a table with the wrong columns."
              },
              {
                "title": "Keep the parts that work",
                "copy": "Tell AI to preserve correct facts or a section you already like. This makes your revision easier to compare."
              },
              {
                "title": "Set a clear target",
                "copy": "Ask for one focused change or a small related set of changes. Then check whether the new draft actually follows the request."
              }
            ]
          },
          {
            "type": "section",
            "title": "Give feedback that makes the next draft better.",
            "copy": "An AI invitation is too formal, too long, and missing the location. Write a follow-up that fixes those issues while preserving the correct date and time."
          },
          {
            "type": "prompt-example",
            "before": "Make it better.",
            "after": "Keep the date and time from the first draft. Make the invitation warm and conversational, shorten it to 80 words, and add the meeting point: the park entrance. End with one sentence asking people to reply if they can attend.",
            "badCaption": "This leaves important details for AI to guess.",
            "goodCaption": "The request gives the task, useful context, and a way to judge the result."
          },
          {
            "type": "section",
            "title": "Try it, then review it",
            "copy": "Open Try it yourself above and write your own version. When you are ready, copy it into Gab AI. Read the answer and ask for one specific improvement."
          },
          {
            "type": "unordered-list",
            "title": "What to check in the result",
            "items": [
              "Did the new draft become warmer and shorter?",
              "Did it preserve the original date and time?",
              "Did it add the park entrance without introducing any new facts?"
            ]
          },
          {
            "type": "quiz",
            "items": [
              {
                "question": "Which feedback is easiest to act on?",
                "options": [
                  "Try harder.",
                  "Make it amazing.",
                  "Keep the date and time. Shorten it to 80 words and use a friendly tone."
                ],
                "correctIndex": 2,
                "explanation": "Specific feedback gives the next draft a target while preserving correct details."
              },
              {
                "question": "The revision fixes the tone but changes the date. What next?",
                "options": [
                  "Accept the improvement.",
                  "Restore the correct date and check the other facts again.",
                  "Restart with no context."
                ],
                "correctIndex": 1,
                "explanation": "An improvement in one area can introduce an error elsewhere. Review the whole result after a revision."
              }
            ]
          },
          {
            "type": "key-takeaways",
            "items": [
              "Name the mismatch.",
              "Keep the parts that work.",
              "Set a clear target."
            ]
          }
        ],
        "ogImage": "guides/social-cards/83b5db08-d261-44a1-ad69-416031f8a22e.png"
      },
      "guideSeo": {
        "readMinutes": 3,
        "difficulty": "Intermediate",
        "about": [
          "Prompt iteration",
          "Prompt chaining",
          "AI workflow"
        ],
        "teaches": [
          "Name the mismatch",
          "Keep the parts that work",
          "Set a clear target"
        ]
      },
      "navigation": {
        "previous": {
          "label": "Give useful context",
          "href": "/docs/learn/providing-context"
        },
        "next": {
          "label": "Check before you trust",
          "href": "/docs/learn/common-mistakes"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/learn/iterating-prompts",
      "updatedAt": "2026-09-26T12:00:00.000Z"
    },
    {
      "guideType": "learn",
      "slug": "common-mistakes",
      "title": "Check before you trust",
      "description": "Separate evidence from assumptions and decide what you need to verify yourself.",
      "badge": "Your first five lessons",
      "badgeColor": "#040404",
      "navSectionSlug": "module-6-best-practices",
      "navOrder": 22,
      "navIcon": "triangle-exclamation",
      "searchKeywords": [],
      "meta": {
        "title": "Common AI Prompting Mistakes—and How to Fix Them",
        "description": "Fix vague prompts, overloaded requests, missing context, bad output formats, and unverified answers with clear before-and-after examples.",
        "keywords": "common AI prompting mistakes, bad AI prompts, why AI prompt is not working, fix AI prompt, AI hallucinations",
        "pageCopy": [
          {
            "type": "section",
            "title": "Start with a real task",
            "copy": "AI can produce a confident answer with missing or incorrect details. A good prompt helps, but it does not replace verification. Match your checks to the task and to the consequences of getting it wrong."
          },
          {
            "type": "steps",
            "items": [
              {
                "title": "Compare with the source",
                "copy": "For a summary, check the original document. For a calculation, recompute key totals. For code, run it and test the important behavior."
              },
              {
                "title": "Mark what is unknown",
                "copy": "Ask which details came from your notes, which came from a cited source, and which are assumptions. Open the source and make sure it supports the claim."
              },
              {
                "title": "Confirm decisions outside the conversation",
                "copy": "Before booking a venue, confirm the price, availability, capacity, and access with the venue itself. Use qualified advice for decisions that require professional judgment."
              }
            ]
          },
          {
            "type": "section",
            "title": "Make a plan for checking an AI answer.",
            "copy": "AI has recommended a venue for your event. Write a follow-up that separates facts from assumptions and identifies what you need to confirm with the venue."
          },
          {
            "type": "prompt-example",
            "before": "Are you sure this venue is good?",
            "after": "Review your venue recommendation. Separate details supported by the notes I supplied from assumptions. List what I must confirm directly: capacity, current availability, total cost, and step-free access. Do not fill in missing facts. Include a short list of questions I can send to the venue.",
            "badCaption": "This leaves important details for AI to guess.",
            "goodCaption": "The request gives the task, useful context, and a way to judge the result."
          },
          {
            "type": "section",
            "title": "Try it, then review it",
            "copy": "Open Try it yourself above and write your own version. When you are ready, copy it into Gab AI. Read the answer and ask for one specific improvement."
          },
          {
            "type": "unordered-list",
            "title": "What to check in the result",
            "items": [
              "Are capacity and price backed by the notes or a source you checked?",
              "Are availability and step-free access confirmed, rather than assumed?",
              "Do you have a short list of questions to resolve before booking?"
            ]
          },
          {
            "type": "quiz",
            "items": [
              {
                "question": "AI says a venue is available next Saturday. What is the strongest check?",
                "options": [
                  "Ask the same AI if it is certain.",
                  "Confirm directly with the venue.",
                  "Look at how confident the answer sounds."
                ],
                "correctIndex": 1,
                "explanation": "The venue can confirm current availability. Repeating a claim does not verify it."
              },
              {
                "question": "A cited page exists but does not support the claim. What should you do?",
                "options": [
                  "Keep the claim because it has a citation.",
                  "Treat the claim as unverified and find relevant evidence.",
                  "Remove every source from the answer."
                ],
                "correctIndex": 1,
                "explanation": "A citation is useful only when the source actually supports the associated claim."
              }
            ]
          },
          {
            "type": "key-takeaways",
            "items": [
              "Compare with the source.",
              "Mark what is unknown.",
              "Confirm decisions outside the conversation."
            ]
          }
        ],
        "ogImage": "guides/social-cards/b37a5a93-5513-46d9-ab55-913f038860c9.png"
      },
      "guideSeo": {
        "readMinutes": 4,
        "difficulty": "Beginner",
        "about": [
          "Prompting mistakes",
          "Prompt engineering",
          "AI troubleshooting"
        ],
        "teaches": [
          "Compare with the source",
          "Mark what is unknown",
          "Confirm decisions outside the conversation"
        ]
      },
      "navigation": {
        "previous": {
          "label": "Improve the first answer",
          "href": "/docs/learn/iterating-prompts"
        },
        "next": {
          "label": "Choose your next skill",
          "href": "/docs#learn"
        }
      },
      "status": "published",
      "canonicalPath": "/docs/learn/common-mistakes",
      "updatedAt": "2026-09-26T12:00:00.000Z"
    }
  ]
}
